import posthog from 'posthog-js';

// Google Ads. The base gtag.js tag is loaded from public/index.html (and, for the
// static blog, blog/templates/blog/_analytics.html) — keep this id in sync with both.
const GOOGLE_ADS_ID = 'AW-18442828438';

// Conversion labels come from the event snippet Google Ads shows when you create
// a conversion action ("AW-18442828438/AbC-D_efGh"); we store only the label half.
// Left unset in dev so test signups on localhost don't report real conversions.
const ConversionLabels = {
  SIGNUP: (process.env.REACT_APP_GOOGLE_ADS_SIGNUP_LABEL || '').trim(),
};

// Centralized analytics event names so we never scatter raw string literals across
// components (same rationale as api.js centralizing backend calls). Keep these names
// in sync with the funnel/retention insights configured in PostHog.
//
// Activation funnel (in order):
//   SIGNUP -> BUILDER_OPENED -> STRATEGY_SAVED -> BACKTEST_STARTED -> BACKTEST_RESULTS_VIEWED
// Retention:
//   PAPER_TRADING_STARTED (the primary return-hook conversion)
//   LOGIN + default $pageview power the D1/D7 return-retention insight.
export const Events = {
  SIGNUP: 'signup_completed',
  LOGIN: 'login_completed',
  BUILDER_OPENED: 'builder_opened',
  STRATEGY_SAVED: 'strategy_saved',
  BACKTEST_STARTED: 'backtest_started',
  BACKTEST_RESULTS_VIEWED: 'backtest_results_viewed',
  // Distinguish a real result from a blank one: the retention analysis showed a
  // 0-trade result churns like a failure, so we track the two outcomes separately.
  BACKTEST_TRADED: 'backtest_traded',
  BACKTEST_EMPTY: 'backtest_empty',
  EMPTY_BACKTEST_FIX_CLICKED: 'empty_backtest_fix_clicked',
  EMPTY_BACKTEST_SUGGEST_CLICKED: 'empty_backtest_suggest_clicked',
  EMPTY_BACKTEST_IMPLEMENT_CLICKED: 'empty_backtest_implement_clicked',
  EMPTY_BACKTEST_QUICKEDIT_CLICKED: 'empty_backtest_quickedit_clicked',
  PAPER_TRADING_STARTED: 'paper_trading_started',
  BACKTEST_SHARE_OPENED: 'backtest_share_opened',
  BACKTEST_DATA_DOWNLOAD_OPENED: 'backtest_data_download_opened',
  BACKTEST_DATA_DOWNLOADED: 'backtest_data_downloaded',
  BACKTEST_SHARE_COPIED: 'backtest_share_copied',
  BACKTEST_STRATEGY_SAVED: 'backtest_strategy_saved',
  STRATEGY_EXPORT_OPENED: 'strategy_export_opened',
  STRATEGY_EXPORTED: 'strategy_exported',
};

// Fire-and-forget event capture. Analytics must never break the app, so all calls
// are wrapped — a misconfigured/blocked PostHog should be a no-op, not a crash.
export function track(event, properties = {}) {
  try {
    posthog.capture(event, properties);
  } catch {
    /* no-op */
  }
}

// Report a Google Ads conversion. Same fire-and-forget contract as track().
//
// Signup is reported here client-side because it happens in-session. Purchases
// are NOT — they can land weeks after the ad click, past the point where the
// browser still holds the click id, so they are imported server-side from the
// Stripe webhook instead. See users/management/commands/export_ads_conversions.py.
export function trackAdsConversion(name, params = {}) {
  const label = ConversionLabels[name];
  // No label configured, or gtag blocked/not yet loaded — nothing to report.
  if (!label || typeof window === 'undefined' || !window.gtag) return;
  try {
    window.gtag('event', 'conversion', { send_to: `${GOOGLE_ADS_ID}/${label}`, ...params });
  } catch {
    /* no-op */
  }
}

// Report an OpenAI Ads conversion. The oaiq pixel is loaded from
// public/index.html; same fire-and-forget contract as trackAdsConversion().
export function trackOpenAIConversion(event, params = { type: 'customer_action' }) {
  if (typeof window === 'undefined' || !window.oaiq) return;
  try {
    window.oaiq('measure', event, params);
  } catch {
    /* no-op */
  }
}

// Set durable person properties (e.g. signup_date) for cohort/retention analysis.
export function setPersonProps(props = {}) {
  try {
    posthog.setPersonProperties(props);
  } catch {
    /* no-op */
  }
}
